import type { ManufacturerCategory } from "randmar-api-client";
import {
  useGetV4ManufacturerByRouteManufacturerIdCategoriesQuery,
  usePutV4ManufacturerByRouteManufacturerIdCategoryAndCategoryCodeMutation,
} from "randmar-api-client";
import React from "react";
import {
  Button,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Input,
  Label,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Skeleton,
  useToast,
} from "randmarcomps";
import { useManufacturer } from "../../contexts/WithManufacturer";

interface IProps {
  selectedCategory: string;
  onSelectedCategoryChange: (newCategory: string) => void;
  onMenuClick?: (text: string) => void;
  // Note for AI: categories was removed only because it was unused. If that wasn't the case, we would have supported both the old and new types like ReturnGrid.tsx
  defaultValue?: string;
  disabled?: boolean;
}

const labelClassName = "text-xs font-semibold uppercase tracking-wide text-gray-500";
const inputClassName =
  "h-11 w-full rounded-lg border border-gray-200 bg-white px-3 text-[15px] text-gray-800 shadow-sm outline-none focus-visible:ring-2 focus-visible:ring-[var(--ops-focus-ring)]";

const CategorySelector: React.FC<IProps> = ({ selectedCategory, onSelectedCategoryChange, onMenuClick, defaultValue, disabled }) => {
  const { applicationId } = useManufacturer();
  const [categoryName, setCategoryName] = React.useState("");
  const [open, setOpen] = React.useState(false);
  const { toast } = useToast();

  const categoriesQuery = useGetV4ManufacturerByRouteManufacturerIdCategoriesQuery({ routeManufacturerId: applicationId });
  const categories = categoriesQuery.data ?? [];
  const [createCategory, { isLoading: isCreating }] = usePutV4ManufacturerByRouteManufacturerIdCategoryAndCategoryCodeMutation();

  const sortedCategories = React.useMemo(
    () => [...categories].sort((a, b) => (a.CategoryName || "").localeCompare(b.CategoryName || "")),
    [categories]
  );

  const trimmedCategoryName = categoryName.trim();
  const categoryAlreadyExists = trimmedCategoryName
    ? sortedCategories.some((entry) => (entry.CategoryName || "").trim().toLowerCase() === trimmedCategoryName.toLowerCase())
    : false;

  React.useEffect(() => {
    if (defaultValue && categories) {
      const defaultCategory = (categories as ManufacturerCategory[]).find((entry) => entry.CategoryName === defaultValue);
      if (defaultCategory?.CategoryCode) {
        onSelectedCategoryChange(defaultCategory.CategoryCode);
      }
    }
  }, [defaultValue, categories, onSelectedCategoryChange]);

  const handleCancel = React.useCallback(() => {
    setOpen(false);
    setCategoryName("");
  }, []);

  const handleCreateCategory = React.useCallback(async () => {
    if (!trimmedCategoryName || categoryAlreadyExists) return;
    try {
      await createCategory({
        routeManufacturerId: applicationId,
        categoryCode: trimmedCategoryName,
      }).unwrap();
      toast({ variant: "success", title: "Category created", description: `${trimmedCategoryName} is now available.` });
      setCategoryName("");
      setOpen(false);
    } catch {
      toast({ variant: "destructive", title: "Could not create category", description: "Please try again." });
    }
  }, [applicationId, categoryAlreadyExists, createCategory, toast, trimmedCategoryName]);

  if (categoriesQuery.isLoading) {
    return (
      <div className="w-full space-y-2">
        <Label htmlFor="category-selector" className={labelClassName}>
          Category <span className="text-rose-500">*</span>
        </Label>
        <Skeleton className="h-11 w-full rounded-lg" />
      </div>
    );
  }

  return (
    <>
      <div className="w-full space-y-2">
        <Label htmlFor="category-selector" className={labelClassName}>
          Category <span className="text-rose-500">*</span>
        </Label>
        <Select
          required
          value={selectedCategory}
          onValueChange={onSelectedCategoryChange}
          disabled={disabled}
        >
          <SelectTrigger id="category-selector" className={inputClassName}>
            <SelectValue placeholder="Select a category" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem
              value="add-new-category"
              onSelect={(e) => { e.preventDefault(); setOpen(true); }}
              className="font-medium text-[color:var(--ops-accent)]"
            >
              + Create a category
            </SelectItem>
            {sortedCategories.map((category) => (
              <SelectItem
                key={`Category:${category.CategoryCode ?? category.CategoryName ?? "unknown"}`}
                value={category.CategoryCode || ""}
                onSelect={() => (onMenuClick ? onMenuClick(category.CategoryName || "") : undefined)}
              >
                {category.CategoryName}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      <Dialog open={open} onOpenChange={(isOpen) => (!isOpen ? handleCancel() : setOpen(isOpen))}>
        <DialogContent className="sm:max-w-[425px]">
          <DialogHeader>
            <DialogTitle>Create category</DialogTitle>
            <DialogDescription>Add a new category to your catalog.</DialogDescription>
          </DialogHeader>
          <div className="space-y-2 py-2">
            <Label htmlFor="category-name" className={labelClassName}>
              Category name
            </Label>
            <Input
              id="category-name"
              value={categoryName}
              onChange={(event) => setCategoryName(event.target.value)}
              className={inputClassName}
              disabled={isCreating}
              placeholder="e.g. Audio / Headphones"
              autoComplete="off"
            />
            {categoryAlreadyExists && <p className="text-[13px] text-rose-600">That category already exists.</p>}
          </div>
          <DialogFooter className="sm:justify-between">
            <Button variant="outline" onClick={handleCancel}>
              Cancel
            </Button>
            <Button
              onClick={handleCreateCategory}
              disabled={isCreating || !trimmedCategoryName || categoryAlreadyExists}
            >
              {isCreating ? "Creating..." : "Create"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default CategorySelector;
